// BM-H597 (23 Sep 2026): take a reviewed client care letter back to draft.
//
// Shown on the CCL editor once the letter is reviewed, and hidden for good
// once any client has signed. The fee earner gives a reason (at least ten
// characters). If the letter has been sent, the dialog says the client's
// signing link will stop working, because the server revokes it.
//
// The server decides who may do this (owning fee earner or a director) and
// refuses it after a signature; this component only reports what it says.
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../config/api";
import { errorMessage } from "../utils/errorMessage";

export const REVERT_REASON_MIN = 10;

export default function CclRevertToDraft({
  submissionId,
  sent = false,
  clientSigned = false,
  onReverted,
}) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  if (clientSigned) return null;

  const trimmed = reason.trim();
  const reasonOk = trimmed.length >= REVERT_REASON_MIN;

  const submit = async () => {
    if (!reasonOk || busy) return;
    setBusy(true);
    try {
      const res = await axios.post(
        `${API}/submissions/${submissionId}/revert-ccl-to-draft`,
        { reason: trimmed },
      );
      toast.success("Letter taken back to draft. You can edit it again");
      setOpen(false);
      setReason("");
      onReverted?.(res?.data || {});
    } catch (err) {
      toast.error(errorMessage(err, "Could not take the letter back to draft"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="text-xs px-3 py-1.5 rounded border border-amber-400 text-amber-800 hover:bg-amber-50"
        data-testid="revert-to-draft-btn"
      >
        Revert to draft
      </button>
      {open && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="ccl-revert-title"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
          data-testid="revert-to-draft-dialog"
        >
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-5 space-y-3">
            <h2 id="ccl-revert-title" className="text-base font-semibold text-slate-800">
              Revert to draft
            </h2>
            <p className="text-sm text-slate-600">
              The review and your signature on the letter will be cleared. The
              reviewed version stays in the version history.
            </p>
            {sent && (
              <p className="text-sm text-amber-800" data-testid="revert-link-warning">
                The client&apos;s signing link will stop working.
              </p>
            )}
            <label htmlFor="ccl-revert-reason" className="block text-sm font-medium text-slate-700">
              Reason
            </label>
            <textarea
              id="ccl-revert-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              className="w-full border border-slate-300 rounded p-2 text-sm"
              data-testid="revert-reason-input"
            />
            {!reasonOk && (
              <p className="text-xs text-slate-500">
                At least {REVERT_REASON_MIN} characters.
              </p>
            )}
            <div className="flex justify-end gap-2 pt-1">
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="text-sm px-3 py-1.5 rounded border border-slate-300"
                data-testid="revert-cancel-btn"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={submit}
                disabled={!reasonOk || busy}
                className="text-sm px-3 py-1.5 rounded bg-amber-600 text-white disabled:opacity-50"
                data-testid="revert-confirm-btn"
              >
                {busy ? "Reverting" : "Revert to draft"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
